/**
 * StoreOnboardingApply (CLA-993) — the wholesale-account application shown on
 * the locked store page (StoreGate) to users who can manage the clinic.
 *
 * The regulatory floor as a short form: ABN (verified server-side against the
 * ABR at submit), business delivery address, and a business-premises
 * attestation — scheduled medicines can't be delivered to a residential
 * address. Fresh Clinics reviews and approval unlocks the store.
 */
import { useState } from 'react'
import { Link } from 'react-router'
import { CheckCircle2, Clock, Sparkles, XCircle } from 'lucide-react'
import { ApiError } from '@/api/client'
import {
  useSaveStoreOnboarding,
  useStoreOnboarding,
  useSubmitStoreOnboarding,
  type StoreOnboardingApplication,
  type StoreOnboardingDraft,
} from '@/api/store-onboarding'
import { useSubscription, type Subscription } from '@/api/subscription-hooks'

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA']

const inputClass =
  'w-full px-3 py-2 rounded-lg border border-border bg-surface text-sm text-ink ' +
  'placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-claro-500/40'

/**
 * CLA-1034 (first slice): name the TIER gate before the paperwork. This form
 * is the regulatory gate (wholesale account); ordering ALSO needs the Clinic
 * plan, and until this note existed a clinic on a lower tier learned that
 * only after filling in the application (a real founder hit exactly this on
 * launch night). Copy is state-aware and never conflates the two gates.
 */
function TierNote({ sub }: { sub: Subscription }) {
  const hasStore = (sub.features ?? []).includes('store')

  let body: React.ReactNode = null
  if (sub.plan === 'early_access') {
    body = (
      <>
        A heads up for when you choose your plan: ordering is part of the{' '}
        <strong className="font-semibold">Clinic</strong> plan.
      </>
    )
  } else if (sub.status === 'trialing') {
    body = (
      <>
        Ordering is included in your trial. Keeping it afterwards means choosing the{' '}
        <strong className="font-semibold">Clinic</strong> plan.
      </>
    )
  } else if (!hasStore) {
    body = (
      <>
        Ordering is part of the <strong className="font-semibold">Clinic</strong> plan. This
        application opens your wholesale account — you'll also need the Clinic plan to place
        orders.{' '}
        <Link to="/settings/plan" className="font-medium link">
          Compare plans
        </Link>
      </>
    )
  }

  if (!body) return null

  return (
    <div className="mb-4 flex items-start gap-2 rounded-xl border border-claro-200 bg-claro-50 px-4 py-3">
      <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-claro-600" />
      <p className="text-xs text-ink-secondary leading-relaxed">{body}</p>
    </div>
  )
}

function draftFromApplication(app: StoreOnboardingApplication | null): StoreOnboardingDraft {
  return {
    abn: app?.abn ?? '',
    legal_name: app?.legal_name ?? '',
    trading_name: app?.trading_name ?? '',
    delivery_address_line1: app?.delivery_address_line1 ?? '',
    delivery_address_line2: app?.delivery_address_line2 ?? '',
    delivery_suburb: app?.delivery_suburb ?? '',
    delivery_state: app?.delivery_state ?? '',
    delivery_postcode: app?.delivery_postcode ?? '',
    business_premises_attested: app?.business_premises_attested ?? false,
  }
}

export function StoreOnboardingApply() {
  const { data, isLoading } = useStoreOnboarding()
  const { data: subscription } = useSubscription()
  const save = useSaveStoreOnboarding()
  const submit = useSubmitStoreOnboarding()

  const app = data?.application ?? null
  const [form, setForm] = useState<StoreOnboardingDraft>(() => draftFromApplication(null))
  const [seededFromId, setSeededFromId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Seed the form once per application row, adjusted during render (the React
  // "state from props" pattern — an effect here trips the cascading-render
  // lint). Draft edits must not be clobbered by refetches; a new row after a
  // rejection re-seeds.
  if (app && app.id !== seededFromId && app.status === 'draft') {
    setForm(draftFromApplication(app))
    setSeededFromId(app.id)
  }

  if (isLoading) return null

  if (app?.status === 'submitted') {
    return (
      <div className="mt-2 mb-8 mx-auto max-w-md text-left">
        <div className="rounded-xl border border-border bg-surface-secondary px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-medium text-ink">
            <Clock className="w-4 h-4 text-claro-600" />
            Your application is under review
          </p>
          <p className="mt-1.5 text-xs text-ink-secondary leading-relaxed">
            The Fresh Clinics team is reviewing your store access application (ABN{' '}
            {app.abn ?? '—'}). The store unlocks here automatically once it's approved.
          </p>
        </div>
        {/* The tier gate stays visible while the regulatory gate is pending —
            approval alone doesn't switch ordering on for a non-Clinic plan. */}
        <div className="mt-4">{subscription && <TierNote sub={subscription} />}</div>
      </div>
    )
  }

  if (app?.status === 'approved') {
    // Normally unreachable (an approved clinic is unlocked), but render
    // something sane rather than an apply form if the caches disagree.
    return (
      <p className="mt-2 mb-8 flex items-center justify-center gap-2 text-sm text-ink-secondary">
        <CheckCircle2 className="w-4 h-4 text-success" /> Store access approved — reload to
        start ordering.
      </p>
    )
  }

  const set = (field: keyof StoreOnboardingDraft) => (value: string | boolean) =>
    setForm(prev => ({ ...prev, [field]: value }))

  const handle = async (action: 'save' | 'submit') => {
    setError(null)
    try {
      await save.mutateAsync(form)
      if (action === 'submit') await submit.mutateAsync()
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Something went wrong — try again.')
    }
  }

  const busy = save.isPending || submit.isPending

  return (
    <div className="mt-2 mb-8 mx-auto max-w-md text-left">
      {subscription && <TierNote sub={subscription} />}
      {app?.status === 'rejected' && (
        <div className="mb-4 rounded-xl border border-error/25 bg-error-light px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-medium text-on-error-soft">
            <XCircle className="w-4 h-4" /> Your application needs another look
          </p>
          {app.reject_reason && (
            <p className="mt-1 text-xs text-on-error-soft">{app.reject_reason}</p>
          )}
          <p className="mt-1 text-xs text-on-error-soft">
            Fix the details below and submit again.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-border bg-surface px-5 py-4">
        <h2 className="text-sm font-semibold text-ink mb-1">Apply for store access</h2>
        <p className="text-xs text-ink-secondary leading-relaxed mb-4">
          Ordering is supplied wholesale by Fresh Clinics, so we verify your business before
          switching it on. Usually reviewed within a business day.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-ink-secondary mb-1">ABN</label>
            <input
              className={inputClass}
              value={form.abn ?? ''}
              onChange={e => set('abn')(e.target.value)}
              placeholder="11 digits"
              inputMode="numeric"
            />
            <p className="mt-1 text-[11px] text-ink-faint">
              Checked against the Australian Business Register.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-secondary mb-1">
              Delivery address
            </label>
            <div className="space-y-2">
              <input
                className={inputClass}
                value={form.delivery_address_line1 ?? ''}
                onChange={e => set('delivery_address_line1')(e.target.value)}
                placeholder="Street address"
                autoComplete="address-line1"
              />
              <input
                className={inputClass}
                value={form.delivery_address_line2 ?? ''}
                onChange={e => set('delivery_address_line2')(e.target.value)}
                placeholder="Suite, unit, level (optional)"
                autoComplete="address-line2"
              />
              <div className="grid grid-cols-[1fr_5.5rem_5.5rem] gap-2">
                <input
                  className={inputClass}
                  value={form.delivery_suburb ?? ''}
                  onChange={e => set('delivery_suburb')(e.target.value)}
                  placeholder="Suburb"
                />
                <select
                  className={inputClass}
                  value={form.delivery_state ?? ''}
                  onChange={e => set('delivery_state')(e.target.value)}
                >
                  <option value="">State</option>
                  {AU_STATES.map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <input
                  className={inputClass}
                  value={form.delivery_postcode ?? ''}
                  onChange={e => set('delivery_postcode')(e.target.value)}
                  placeholder="Postcode"
                  inputMode="numeric"
                  maxLength={4}
                />
              </div>
            </div>
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-border"
              checked={form.business_premises_attested ?? false}
              onChange={e => set('business_premises_attested')(e.target.checked)}
            />
            <span className="text-xs text-ink-secondary leading-relaxed">
              I confirm this delivery address is a business premises. Scheduled medicines
              can't be delivered to a residential address.
            </span>
          </label>
        </div>

        {error && <p className="mt-3 text-xs text-on-error-soft">{error}</p>}

        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={() => handle('submit')}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-claro-600 hover:bg-claro-700 disabled:bg-primary-disabled disabled:text-on-primary-disabled disabled:cursor-not-allowed text-white text-sm font-medium"
          >
            {busy ? 'Submitting…' : 'Submit for review'}
          </button>
          <button
            onClick={() => handle('save')}
            disabled={busy}
            className="px-3 py-2 text-sm text-ink-secondary hover:text-ink disabled:opacity-50"
          >
            Save draft
          </button>
        </div>
      </div>
    </div>
  )
}
